import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Header } from '../components/ui/header';
import { Footer } from '../components/ui/footer';
import { WhatsAppCTA } from '../components/ui/whatsapp-cta';
import { useTaxonomy } from '../hooks/useTaxonomy';
import { API_BASE } from '../config/api';
import { getTitlePlainText } from '../utils/titleFormat';

export const BlogListing = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState(searchParams.get('category') || 'All');
  const [currentPage, setCurrentPage] = useState(1);
  const [currentMobileSlide, setCurrentMobileSlide] = useState(0);

  const backendUrl = API_BASE;
  const blogsPerPage = 16; // 4 rows × 4 columns
  
  // Fetch categories from taxonomy
  const { data: taxonomyData, loading: taxonomyLoading } = useTaxonomy('article_category');
  const categories = ['All', ...taxonomyData.map(item => item.label)];

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${backendUrl}/api/public/blogs`);
        if (!response.ok) throw new Error('Failed to fetch blogs');
        const data = await response.json();
        setBlogs(data.blogs || []);
        setFilteredBlogs(data.blogs || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, [backendUrl]);

  useEffect(() => {
    if (activeCategory === 'All') {
      setFilteredBlogs(blogs);
    } else {
      setFilteredBlogs(blogs.filter(blog => blog.category === activeCategory));
    }
    setCurrentPage(1);
    setCurrentMobileSlide(0);
  }, [activeCategory, blogs]);

  // Auto-play carousel on mobile
  useEffect(() => {
    if (filteredBlogs.length <= 4) return;
    
    const interval = setInterval(() => {
      setCurrentMobileSlide((prev) => {
        const maxSlide = Math.min(filteredBlogs.length, 4) - 1;
        return prev >= maxSlide ? 0 : prev + 1;
      });
    }, 4000); // Change slide every 4 seconds

    return () => clearInterval(interval);
  }, [filteredBlogs.length]);

  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    // Update URL params
    if (category !== 'All') {
      setSearchParams({ category });
    } else {
      setSearchParams({});
    }
  };

  const handleClearFilter = () => {
    setActiveCategory('All');
    setSearchParams({});
  };

  const handleMobileSlideChange = (index) => {
    setCurrentMobileSlide(index);
  };

  const indexOfLastBlog = currentPage * blogsPerPage;
  const indexOfFirstBlog = indexOfLastBlog - blogsPerPage;
  const currentBlogs = filteredBlogs.slice(indexOfFirstBlog, indexOfLastBlog);
  const mobileBlogs = filteredBlogs.slice(0, 4); // Show first 4 for mobile carousel
  const totalPages = Math.ceil(filteredBlogs.length / blogsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading || taxonomyLoading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-[#F97316] border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-[#4B5563]">Loading guides...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-[#111827] mb-4">Unable to Load Guides</h1>
            <p className="text-[#4B5563] mb-6">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-[#F97316] hover:bg-[#fb7e2e] text-white font-semibold rounded-lg transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col overflow-x-hidden">
      {/* SEO Meta Tags for Blog Listing Page */}
      <Helmet>
        <title>Study Abroad Guides & Scholarships | Unisave</title>
        <meta 
          name="description" 
          content="Expert study abroad guides, scholarships, visa tips, and university insights to help you save on higher education." 
        />
      </Helmet>

      <Header />
      
      <main className="flex-1 w-full max-w-full px-6 md:px-8 lg:px-12">
        {/* PAGE HEADER */}
        <section className="py-10 md:py-14 w-full max-w-[1280px] mx-auto">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-[#111827] mb-5">Guides & Insights</h1>
            <p className="text-lg md:text-xl text-[#6B7280] max-w-3xl mx-auto leading-relaxed">
              Expert advice and comprehensive guides to help you navigate your education journey
            </p>
          </div>
        </section>

        {/* CATEGORY FILTER BAR */}
        <section className="py-6 w-full max-w-[1280px] mx-auto">
          <div className="flex flex-wrap items-center gap-3 justify-center">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => handleCategoryChange(category)}
                data-testid={`category-filter-${category.toLowerCase().replace(' ', '-')}`}
                className={`px-5 py-2.5 rounded-full font-semibold text-sm transition-all ${
                  activeCategory === category
                    ? 'bg-[#1E40AF] text-white shadow-lg scale-105'
                    : 'bg-white text-[#4B5563] border-2 border-[#E5E7EB] hover:border-[#1E40AF] hover:shadow-md'
                }`}
              >
                {category}
              </button>
            ))}
            {activeCategory !== 'All' && (
              <button
                onClick={handleClearFilter}
                data-testid="clear-filter-button"
                className="px-5 py-2.5 text-[#F97316] hover:text-[#fb7e2e] font-semibold text-sm flex items-center gap-2 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Clear Filter
              </button>
            )}
          </div>
        </section>

        {/* HORIZONTAL AD BANNER */}
        <section className="py-8 w-full max-w-[1280px] mx-auto">
          <div className="w-full h-32 bg-gradient-to-br from-[#F8FAFC] to-white border-2 border-dashed border-[#E5E7EB] rounded-2xl flex items-center justify-center">
            <div className="text-center text-[#9CA3AF]">
              <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-sm font-medium">Horizontal Ad Banner (728×90)</p>
            </div>
          </div>
        </section>

        {/* BLOG CARDS GRID */}
        <section className="py-6 w-full max-w-[1280px] mx-auto">
          {filteredBlogs.length === 0 ? (
            <div className="text-center py-16">
              <svg className="w-20 h-20 mx-auto text-[#9CA3AF] mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="text-2xl font-bold text-[#111827] mb-3">No Guides Found</h3>
              <p className="text-[#6B7280] mb-8 text-lg">We couldn't find any guides matching your criteria</p>
              <button
                onClick={() => window.open('https://wa.me/60123456789?text=Hi, I would like to speak with an advisor', '_blank')}
                data-testid="consult-cta-button"
                className="px-8 py-4 bg-gradient-to-r from-[#F97316] to-[#fb7e2e] hover:shadow-xl text-white font-semibold rounded-xl transition-all"
              >
                Consult Us
              </button>
            </div>
          ) : (
            <>
              {/* Desktop Grid: 4x4 */}
              <div className="hidden md:grid md:grid-cols-3 lg:grid-cols-4 gap-6">
                {currentBlogs.map(blog => (
                  <div
                    key={blog.id}
                    onClick={() => navigate(`/blogs/${blog.slug || blog.id}`)}
                    data-testid={`blog-card-${blog.slug}`}
                    className={`bg-white rounded-xl overflow-hidden border-2 transition-all cursor-pointer flex flex-col h-full group ${
                      blog.is_featured 
                        ? 'border-[#059669] shadow-md hover:shadow-xl' 
                        : 'border-[#E5E7EB] hover:border-[#F97316] hover:shadow-lg'
                    }`}
                  >
                    {/* Featured Image */}
                    <div className="relative w-full pt-[56.25%] bg-[#F8FAFC]">
                      {blog.featured_image ? (
                        <img 
                          src={blog.featured_image} 
                          alt={getTitlePlainText(blog.title)}
                          loading="lazy"
                          className="absolute top-0 left-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
                          <svg className="w-12 h-12 text-[#9CA3AF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                      )}
                      {blog.is_featured && (
                        <div className="absolute top-3 right-3 bg-gradient-to-r from-[#059669] to-[#10b981] text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg flex items-center gap-1">
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                          Featured
                        </div>
                      )}
                    </div>
                    
                    {/* Card Content */}
                    <div className="p-4 flex flex-col flex-1">
                      <span className="inline-block px-2.5 py-1 bg-[#E0F2FE] text-[#0369A1] text-xs font-semibold rounded-full mb-3 self-start">
                        {blog.category || 'General'}
                      </span>
                      
                      <h3 className="text-base font-bold text-[#111827] mb-2 line-clamp-2 leading-snug group-hover:text-[#F97316] transition-colors">
                        {getTitlePlainText(blog.title)}
                      </h3>
                      
                      <p className="text-[#6B7280] text-sm mb-4 line-clamp-2 flex-1 leading-relaxed">
                        {blog.excerpt || 'Read this comprehensive guide to learn more...'}
                      </p>
                      
                      <div className="flex items-center justify-between mt-auto pt-3 border-t border-[#E5E7EB]">
                        <span className="text-[#F97316] hover:text-[#fb7e2e] font-semibold text-xs flex items-center gap-1.5 transition-colors">
                          Read More
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </span>
                        {blog.is_featured && (
                          <span className="text-xs text-[#059669] font-semibold">Must Read</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Mobile Carousel */}
              <div className="md:hidden">
                <div className="relative w-full overflow-hidden">
                  <div 
                    className="flex transition-transform duration-500 ease-in-out"
                    style={{ transform: `translateX(-${currentMobileSlide * 100}%)` }}
                  >
                    {mobileBlogs.map(blog => (
                      <div
                        key={blog.id}
                        className="w-full flex-shrink-0 px-2"
                      >
                        <div
                          onClick={() => navigate(`/blogs/${blog.slug || blog.id}`)}
                          data-testid={`blog-card-mobile-${blog.slug}`}
                          className={`bg-white rounded-xl overflow-hidden border-2 transition-all cursor-pointer ${
                            blog.is_featured 
                              ? 'border-[#059669] shadow-md' 
                              : 'border-[#E5E7EB] hover:border-[#F97316] hover:shadow-lg'
                          }`}
                        >
                          {/* Featured Image */}
                          <div className="relative w-full pt-[56.25%] bg-[#F8FAFC]">
                            {blog.featured_image ? (
                              <img 
                                src={blog.featured_image} 
                                alt={getTitlePlainText(blog.title)}
                                loading="lazy"
                                className="absolute top-0 left-0 w-full h-full object-cover"
                              />
                            ) : (
                              <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
                                <svg className="w-12 h-12 text-[#9CA3AF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                              </div>
                            )}
                            {blog.is_featured && (
                              <div className="absolute top-3 right-3 bg-gradient-to-r from-[#059669] to-[#10b981] text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg flex items-center gap-1">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                                Featured
                              </div>
                            )}
                          </div>
                          
                          {/* Card Content */}
                          <div className="p-4">
                            <span className="inline-block px-2.5 py-1 bg-[#E0F2FE] text-[#0369A1] text-xs font-semibold rounded-full mb-2">
                              {blog.category || 'General'}
                            </span>
                            
                            <h3 className="text-lg font-bold text-[#111827] mb-2 line-clamp-2">
                              {getTitlePlainText(blog.title)}
                            </h3>
                            
                            <p className="text-[#6B7280] text-sm mb-3 line-clamp-2">
                              {blog.excerpt || 'Read this comprehensive guide to learn more...'}
                            </p>
                            
                            <button className="text-[#F97316] hover:text-[#fb7e2e] font-semibold text-sm flex items-center gap-2">
                              Read More
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Carousel Indicators */}
                {mobileBlogs.length > 0 && (
                  <div className="flex justify-center gap-2 mt-6">
                    {mobileBlogs.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => handleMobileSlideChange(index)}
                        className={`w-2.5 h-2.5 rounded-full transition-all ${
                          currentMobileSlide === index 
                            ? 'bg-[#F97316] w-8' 
                            : 'bg-[#E5E7EB] hover:bg-[#9CA3AF]'
                        }`}
                        aria-label={`Go to slide ${index + 1}`}
                      />
                    ))}
                  </div>
                )}

                {/* View All Button */}
                {filteredBlogs.length > 4 && (
                  <div className="text-center mt-8">
                    <button
                      onClick={() => navigate('/blogs')}
                      className="px-8 py-3 bg-gradient-to-r from-[#F97316] to-[#fb7e2e] hover:shadow-xl text-white font-semibold rounded-xl transition-all"
                    >
                      View All {filteredBlogs.length} Guides
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </section>

        {/* DESKTOP PAGINATION */}
        {filteredBlogs.length > 0 && (
          <section className="hidden md:block py-10 w-full max-w-[1280px] mx-auto">
            <div className="flex justify-center items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                data-testid="pagination-prev"
                className="px-4 py-2 border border-[#E5E7EB] rounded-lg text-[#4B5563] hover:border-[#1E40AF] hover:text-[#1E40AF] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              {[...Array(totalPages)].map((_, index) => (
                <button
                  key={index + 1}
                  onClick={() => handlePageChange(index + 1)}
                  data-testid={`pagination-page-${index + 1}`}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                    currentPage === index + 1
                      ? 'bg-[#1E40AF] text-white'
                      : 'border border-[#E5E7EB] text-[#4B5563] hover:border-[#1E40AF] hover:text-[#1E40AF]'
                  }`}
                >
                  {index + 1}
                </button>
              ))}
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                data-testid="pagination-next"
                className="px-4 py-2 border border-[#E5E7EB] rounded-lg text-[#4B5563] hover:border-[#1E40AF] hover:text-[#1E40AF] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </section>
        )}
      </main>
      
      <Footer />
      <WhatsAppCTA phone="60123456789" message="Hi, I would like guidance on studying abroad" />
    </div>
  );
};

export default BlogListing;
